/**
 * Where a group's patients live: states or Census metros (CBSAs) shaded, ZIPs
 * or metros as bubbles.
 *
 * The area fill carries the chosen metric (patients, per 100k adults, or the
 * census index); bubbles are always absolute patient counts, sized by area.
 * Index uses a diverging ramp on a log scale (½× and 2× are equally far from
 * 1×), gray at "proportional to population".
 *
 * Metro shading draws state outlines underneath, so the land outside every
 * metro still reads as land. On the index, a metro where the clinic would
 * expect fewer than MIN_EXPECTED patients at population share is hatched
 * rather than coloured: with ~1,000 patients spread over 900 areas, a small
 * town's 0 or 1 is noise, and a deep red there would read as a finding.
 */

import { ComposableMap, Geographies, Geography, Marker } from 'react-simple-maps'
import { Tooltip } from 'react-tooltip'
import { STATE_NAMES, stateCodeFromName } from '../../utils/usStates'

const GEO_URL = '/gz_2010_us_040_00_20m.json'
// Built by the mapshaper step documented in backend/scripts/build-census-reference.js.
const METRO_URL = '/cbsa_2023_20m.topo.json'
const MIN_EXPECTED = 3
const HATCH = 'url(#demo-hatch)'

const SEQUENTIAL = ['#cde2fb', '#9ec5f4', '#6da7ec', '#3987e5', '#256abf', '#184f95', '#0d366b']
// Under-represented (red) ← gray → over-represented (blue), equal arms.
const DIVERGING = ['#c43a3a', '#e57b7a', '#f3bcbb', '#f0efec', '#b7d3f6', '#6da7ec', '#2a78d6']
const INDEX_LIMIT = 2 // log2 clamp: ¼× … 4×

const count = v => Number(v || 0).toLocaleString('en-US')
const fmt = {
  patients: v => count(v),
  per100k: v => (v == null ? '–' : v.toFixed(v < 10 ? 2 : 1)),
  index: v => (v == null ? '–' : `${v.toFixed(2)}×`),
}

function sequentialFill(value, max) {
  if (!value || !max) return 'var(--viz-empty)'
  const t = Math.sqrt(value / max)
  return SEQUENTIAL[Math.min(SEQUENTIAL.length - 1, Math.floor(t * SEQUENTIAL.length))]
}

function divergingFill(index) {
  if (index == null) return 'var(--viz-empty)'
  const l = index <= 0 ? -INDEX_LIMIT : Math.max(-INDEX_LIMIT, Math.min(INDEX_LIMIT, Math.log2(index)))
  // Seven steps over [-2, 2]; the middle step covers roughly 0.8×–1.25×.
  const i = Math.round(((l + INDEX_LIMIT) / (2 * INDEX_LIMIT)) * (DIVERGING.length - 1))
  return DIVERGING[i]
}

function LegendBar({ metric, max, areas }) {
  if (metric === 'index') {
    return (
      <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
        <span>¼× fewer</span>
        <div className="flex">{DIVERGING.map(c => <span key={c} className="h-2.5 w-6" style={{ background: c }} />)}</div>
        <span>4× more than population</span>
        {areas === 'metros' && <>
          <svg width="18" height="10" aria-hidden className="ml-2"><rect width="18" height="10" fill={HATCH} stroke="var(--viz-stroke)" /></svg>
          <span>too few patients expected to say</span>
        </>}
      </div>
    )
  }
  return (
    <div className="flex items-center gap-2 text-xs text-muted-foreground">
      <span>0</span>
      <div className="flex">{SEQUENTIAL.map(c => <span key={c} className="h-2.5 w-6" style={{ background: c }} />)}</div>
      <span>{fmt[metric](max)} {metric === 'per100k' ? 'per 100k adults' : 'patients'}</span>
    </div>
  )
}

/**
 * A metro's numbers, including the many with no patients (absent from
 * group.metros). Their index is 0, not null: no patients where the population
 * says to expect some is an answer — unless too few were expected to tell.
 */
function metroValues(props, byMetro, zctaLocated, nationalAdults) {
  const row = byMetro.get(props.cbsa)
  const adults = props.adults
  const expected = adults && nationalAdults ? zctaLocated * (adults / nationalAdults) : null
  if (row) return { ...row, expected }
  return {
    cbsa: props.cbsa,
    name: props.name,
    type: props.type,
    patients: 0,
    adults: adults ?? null,
    per100k: adults ? 0 : null,
    index: adults ? 0 : null,
    expected,
  }
}

export default function PatientMap({ group, metric, bubbles, areas, nationalAdults }) {
  const byState = new Map(group.states.map(s => [s.state, s]))
  const byMetro = new Map(group.metros.map(m => [m.cbsa, m]))
  const zctaLocated = group.coverage.zctaLocated || 0
  const max = areas === 'metros'
    ? Math.max(0, ...group.metros.map(m => m[metric] || 0))
    : Math.max(0, ...group.states.map(s => s[metric] || 0))
  // Both bubble layers share one shape: a point, a count, and tooltip text.
  const points = bubbles === 'zips'
    ? group.zips.map(z => ({
      key: z.zip, lat: z.lat, lng: z.lng, patients: z.patients,
      tip: `<strong>ZIP ${z.zip}</strong><br/>${count(z.patients)} patients · ${fmt.per100k(z.per100k)} per 100k adults (${count(z.adults)} adults)`,
    }))
    : bubbles === 'metros'
      ? group.metros.map(m => ({
        key: m.cbsa, lat: m.lat, lng: m.lng, patients: m.patients,
        tip: `<strong>${m.name}</strong>${m.type === 'micro' ? ' (micro area)' : ''}<br/>${count(m.patients)} patients · ` +
          `${fmt.per100k(m.per100k)} per 100k adults<br/>${fmt.index(m.index)} vs population`,
      }))
      : []
  const bubbleMax = Math.max(1, ...points.map(p => p.patients))
  // Metros aggregate many ZIPs, so they get more room to differ in size.
  const maxRadius = bubbles === 'metros' ? 26 : 14
  const tooltipId = `demo-map-${group.value ?? 'all'}`

  const stateTip = code => {
    const s = byState.get(code)
    const name = STATE_NAMES[code] || code
    if (!s) return `<strong>${name}</strong><br/>No census row`
    return `<strong>${name}</strong><br/>${count(s.patients)} patients · ${s.share == null ? '–' : (s.share * 100).toFixed(1) + '%'} of located<br/>` +
      `${fmt.per100k(s.per100k)} per 100k adults · ${fmt.index(s.index)} vs population`
  }

  const metroTip = m => {
    const head = `<strong>${m.name}</strong>${m.type === 'micro' ? ' (micro area)' : ''}`
    if (m.adults == null) return `${head}<br/>No separate census row (counted in New Haven)`
    const thin = m.expected != null && m.expected < MIN_EXPECTED
    return `${head}<br/>${count(m.patients)} patients · ${count(m.adults)} adults<br/>` +
      `${fmt.per100k(m.per100k)} per 100k adults · ${fmt.index(m.index)} vs population` +
      (thin ? `<br/>${m.expected.toFixed(1)} expected at population share — too few to judge` : '')
  }

  const metroFill = m => {
    if (metric === 'index') {
      if (m.expected == null || m.expected < MIN_EXPECTED) return HATCH
      return divergingFill(m.index)
    }
    return sequentialFill(m[metric], max)
  }

  const hoverStyle = { default: { outline: 'none' }, hover: { outline: 'none', stroke: 'var(--viz-bubble)', strokeWidth: 1.5 }, pressed: { outline: 'none' } }

  return (
    <div className="demographics-viz space-y-2">
      <div className="relative rounded-lg border bg-card">
        <ComposableMap projection="geoAlbersUsa" width={900} height={520} className="h-auto w-full">
          <defs>
            <pattern id="demo-hatch" width="5" height="5" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
              <rect width="5" height="5" fill="var(--viz-empty)" />
              <line x1="0" y1="0" x2="0" y2="5" stroke="var(--viz-stroke)" strokeWidth="1.25" />
            </pattern>
          </defs>
          <Geographies geography={GEO_URL}>
            {({ geographies }) => geographies.map(geo => {
              const code = stateCodeFromName(geo.properties.NAME)
              const s = byState.get(code)
              if (areas === 'metros') {
                // Plain land with state lines; the metros carry the data.
                return (
                  <Geography key={geo.rsmKey} geography={geo} fill="var(--viz-empty)" stroke="var(--viz-stroke)" strokeWidth={0.5}
                    style={{ default: { outline: 'none' }, hover: { outline: 'none' }, pressed: { outline: 'none' } }} />
                )
              }
              const fill = metric === 'index' ? divergingFill(s?.index) : sequentialFill(s?.[metric], max)
              return (
                <Geography
                  key={geo.rsmKey}
                  geography={geo}
                  fill={fill}
                  stroke="var(--viz-ring)"
                  strokeWidth={0.75}
                  data-tooltip-id={tooltipId}
                  data-tooltip-html={stateTip(code)}
                  style={hoverStyle}
                />
              )
            })}
          </Geographies>
          {areas === 'metros' && (
            <Geographies geography={METRO_URL}>
              {({ geographies }) => geographies.map(geo => {
                const m = metroValues(geo.properties, byMetro, zctaLocated, nationalAdults)
                return (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    fill={metroFill(m)}
                    stroke="var(--viz-ring)"
                    strokeWidth={0.4}
                    data-tooltip-id={tooltipId}
                    data-tooltip-html={metroTip(m)}
                    style={hoverStyle}
                  />
                )
              })}
            </Geographies>
          )}
          {points
            // geoAlbersUsa has no place for Puerto Rico or other territories.
            .filter(p => p.lat > 23 || (p.lat > 18.5 && p.lng < -154))
            .map(p => (
              <Marker key={p.key} coordinates={[p.lng, p.lat]}>
                <circle
                  r={Math.max(2, Math.sqrt(p.patients / bubbleMax) * maxRadius)}
                  fill="var(--viz-bubble)"
                  fillOpacity={0.45}
                  stroke="var(--viz-ring)"
                  strokeWidth={1}
                  data-tooltip-id={tooltipId}
                  data-tooltip-html={p.tip}
                />
              </Marker>
            ))}
        </ComposableMap>
        <Tooltip id={tooltipId} />
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <LegendBar metric={metric} max={max} areas={areas} />
        {points.length > 0 && (
          <span className="text-xs text-muted-foreground">
            Bubbles: patients per {bubbles === 'zips' ? 'ZIP' : 'Census metro/micro area'} (area). Largest = {count(bubbleMax)}.
          </span>
        )}
      </div>
    </div>
  )
}
